import { SubmitHelpers } from '@area2k/use-form';
import { useCallback, useState } from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import Form from '@/form';
import TextAreaField from '@/form/TextAreaField';
import { Small } from '@/components/Typography';

type FormValues = {
  comment: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onReject: (comment: string) => Promise<void>;
  title?: string;
  requireComment?: boolean;
  isLoading?: boolean;
  error?: string | null;
};

const RejectModal = ({
  isOpen,
  onClose,
  onReject,
  title = 'Reject Submission',
  requireComment = true,
  isLoading = false,
  error = null,
}: Props) => {
  const [formValues, setFormValues] = useState<FormValues>({
    comment: '',
  });

  const handleSubmit = useCallback(
    async (values: FormValues, { setFormError }: SubmitHelpers) => {
      if (requireComment && (!values.comment || values.comment.trim() === '')) {
        setFormError({
          title: 'Comment Required',
          message: 'Please provide a comment explaining the rejection reason.',
          status: 'danger',
        });
        return;
      }

      try {
        await onReject(values.comment);
        setFormValues({ comment: '' });
        onClose();
      } catch (err: any) {
        setFormError({
          title: 'Error',
          message:
            err.message || 'Failed to reject submission. Please try again.',
          status: 'danger',
        });
      }
    },
    [onReject, onClose, requireComment],
  );

  if (!isOpen) return null;

  return (
    <Modal
      disableClickout
      size="sm"
      title={title}
      wrapperBackground={true}
      onRequestClose={onClose}
    >
      <Card.Section>
        <Form initialValues={formValues} onSubmit={handleSubmit}>
          <TextAreaField
            autoFocus
            required={requireComment}
            callback={(fieldContext) => {
              setFormValues((prev) => ({
                ...prev,
                comment: fieldContext.value || '',
              }));
            }}
            fieldId="comment"
            label={
              requireComment
                ? 'Rejection Reason *'
                : 'Rejection Reason (Optional)'
            }
            placeholder={
              requireComment
                ? 'Please explain why this submission is being rejected...'
                : 'Optional: Provide feedback on why this submission is being rejected...'
            }
          />
          {error && (
            <Stack css={{ marginTop: '12px' }}>
              <Small color="danger">{error}</Small>
            </Stack>
          )}
          <Stack justify="end" gap={12} css={{ marginTop: '20px' }}>
            <Button
              a11yLabel="Cancel"
              appearance="secondary"
              label="Cancel"
              onClick={onClose}
              disabled={isLoading}
            />
            <Button
              a11yLabel="Reject submission"
              appearance="secondary"
              status="danger"
              isLoading={isLoading}
              label="Reject"
              type="submit"
            />
          </Stack>
        </Form>
      </Card.Section>
    </Modal>
  );
};

export default RejectModal;
